import React, { useCallback, useMemo } from 'react';
import { IoWarning } from 'react-icons/io5';

import AssetIcon from '../../../../components/AssetIcon';
import useHealthCheck, {
  ProblemReason,
} from '../../../../hooks/useHealthCheck';

import './index.css';
import { colors } from '../../../../colors';
import { ControlItem } from '../../types';
import { Link } from 'react-router-dom';

const problemTranslation = {
  [ProblemReason.GRTSProfitCalcsServiceOfflne]:
    'Profit calculations service is offline',
  [ProblemReason.GRTSCandleFetcherServiceOfflne]:
    'Candle fetcher service is offline',
  [ProblemReason.GRTSOrderServiceOfflne]: 'Order service is offline',
  [ProblemReason.GRTSControlServiceOfflne]: 'Control service is offline',
  [ProblemReason.FetchingDataPending]:
    'Candle Fetcher storage is not up to date for all bots',

  [ProblemReason.ActiveStatus]: '',
  [ProblemReason.SyncStatus]: '',
};

interface Props {
  setActiveItem: React.Dispatch<React.SetStateAction<ControlItem>>;
}

const Health = (props: Props) => {
  const { setActiveItem } = props;
  const { data } = useHealthCheck();

  const nonActiveBots: Array<any> = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.filter((r) => r.reason === ProblemReason.ActiveStatus);
  }, [data]);

  const syncStatusBots: Array<any> = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.filter((r) => r.reason === ProblemReason.SyncStatus);
  }, [data]);

  const offlineServices = useMemo(() => {
    if (!data) {
      return [];
    }

    const isFetchingTheLatestData = data.filter(
      (r) => r.reason === ProblemReason.FetchingDataPending,
    );

    const offlineServices = data.filter((r) =>
      r.reason.includes('GRTS_SERVICE'),
    );

    return isFetchingTheLatestData.concat(offlineServices);
  }, [data]);

  const handleOfflineBotPress = useCallback(() => {
    setActiveItem(ControlItem.BotStates);
  }, [setActiveItem]);

  if (typeof data === 'undefined') {
    return (
      <div className="health-loading-container">
        <p className="health-loading-container-text">Loading Health Check...</p>
      </div>
    );
  }

  return (
    <div className="health-container">
      {!!offlineServices.length && (
        <>
          <p className="health-container-label">Services Offline</p>
          <div className="health-container-row">
            {offlineServices.map((service) => (
              <div key={service.reason} className="health-container-item">
                <div className="health-container-clean-row health-container-item-top">
                  <IoWarning color={colors.red} size={40} />
                  <div className="health-container-item-info">
                    <div className="health-container-clean-row">
                      <h4 className="urgent-text">URGENT</h4>
                    </div>
                  </div>
                </div>
                <div className="health-container-clean-row">
                  <h4 className="reason-text">
                    {problemTranslation[service.reason]}
                  </h4>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {!!nonActiveBots.length && (
        <>
          <p className="health-container-label">Non active bots</p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 16,
            }}
          >
            {nonActiveBots.map((b) => (
              <div
                key={b.botId}
                className="health-container-item offline-bot"
                onClick={handleOfflineBotPress}
              >
                <div className="health-container-clean-row">
                  <AssetIcon baseAsset={b.baseAsset} width={60} height={60} />

                  <div className="health-container-item-info">
                    <div className="health-container-clean-row">
                      <h4 className="bi-asset-text-label">BaseAsset:</h4>
                      <h4 className="bi-asset-text">{b.baseAsset}</h4>
                    </div>
                    <div className="health-container-clean-row">
                      <h4 className="bi-asset-text-label">Bot ID:</h4>
                      <h4 className="bi-asset-text">{b.botId}</h4>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {!!syncStatusBots.length && (
        <>
          <p className="health-container-label">
            Bots where Profit Complex Params are out of sync
          </p>
          <div className="health-container-row">
            {syncStatusBots.map((syncStatus) => (
              <div
                className="health-container-item"
                key={syncStatus.subscriptionId}
              >
                <Link to={`/bot/${syncStatus.subscriptionId}`}>
                  <div className="health-container-clean-row health-container-item-top">
                    <AssetIcon
                      baseAsset={syncStatus.baseAsset}
                      width={60}
                      height={60}
                    />

                    <div className="health-container-item-info">
                      <div className="health-container-clean-row">
                        <h4 className="bi-asset-text-label">BaseAsset:</h4>
                        <h4 className="bi-asset-text">
                          {syncStatus.baseAsset}
                        </h4>
                      </div>
                      <div className="health-container-clean-row">
                        <h4 className="bi-asset-text-label">Cluster ID:</h4>
                        <h4 className="bi-asset-text">
                          {syncStatus.clusterId}
                        </h4>
                      </div>
                      <div className="health-container-clean-row">
                        <h4 className="bi-asset-text-label">Bot ID:</h4>
                        <h4 className="bi-asset-text">{syncStatus.botId}</h4>
                      </div>
                    </div>
                  </div>

                  <h4 className="bi-asset-text">Extra Info:</h4>
                  <h4 className="bi-asset-text-label health-container-extra-info">
                    {syncStatus.extraInfo}
                  </h4>
                </Link>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(Health);
